import React, { useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  ClickAwayListener,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Tooltip, TooltipPlacement } from '../../../../display/tooltip';
import {
  ColumnMenuContent,
  ColumnMenuDropdown,
  StyledIconButton,
} from '../styled';

export interface ColumnVisibilityMenuProps {
  columns?: Array<{ id: string; label: string; visible: boolean }>;
  onColumnVisibilityChange?: (columnId: string, visible: boolean) => void;
  onColumnsToggle?: () => void;
}

export const ColumnVisibilityMenu: React.FC<ColumnVisibilityMenuProps> = ({
  columns = [],
  onColumnVisibilityChange,
  onColumnsToggle,
}) => {
  const theme = useTheme();
  const [isColumnsMenuOpen, setIsColumnsMenuOpen] = useState(false);
  const columnsButtonRef = useRef<HTMLButtonElement>(null);

  const handleColumnsClick = () => {
    setIsColumnsMenuOpen(!isColumnsMenuOpen);
    onColumnsToggle?.();
  };

  const handleColumnsMenuClose = () => {
    setIsColumnsMenuOpen(false);
  };

  const handleColumnToggle = (columnId: string, visible: boolean) => {
    onColumnVisibilityChange?.(columnId, visible);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip
        title={isColumnsMenuOpen ? 'Close columns menu' : 'Show/hide columns'}
        placement={TooltipPlacement.Top}
      >
        <StyledIconButton ref={columnsButtonRef} onClick={handleColumnsClick}>
          <SystemIcon
            iconName={
              isColumnsMenuOpen ? 'ColumnsPlusRightClose' : 'ColumnsPlusRight'
            }
            style={{
              color: theme.palette.text.main,
              fontSize: '18px',
            }}
          />
        </StyledIconButton>
      </Tooltip>

      {isColumnsMenuOpen && (
        <ClickAwayListener onClickAway={handleColumnsMenuClose}>
          <ColumnMenuDropdown>
            <ColumnMenuContent>
              {columns.map((column) => (
                <FormControlLabel
                  key={column.id}
                  control={
                    <Checkbox
                      checked={column.visible}
                      onChange={(e) =>
                        handleColumnToggle(column.id, e.target.checked)
                      }
                      size="small"
                      sx={{
                        color: theme.palette.text.main,
                        '&.Mui-checked': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                  }
                  label={column.label}
                  sx={{
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      fontFamily: 'General Sans',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '16px',
                      lineHeight: '150%',
                      letterSpacing: '0.5%',
                      color: theme.palette.text.secondary,
                    },
                  }}
                />
              ))}
            </ColumnMenuContent>
          </ColumnMenuDropdown>
        </ClickAwayListener>
      )}
    </Box>
  );
};
